
/**
 * Checkout: Address lookup
 * ------------------------------------------------------------------------------
 * Checkout shipping address lookup.
 * - Uses Loqate API to add address lookup.
 *
 * @namespace loqateValidator
 */

import axios from 'axios';
import {decodeHTMLEntities} from '~/helpers/utils';
import featureScroll from './feature-carousel';

/**
 * DOM selectors.
 */
const selectors = {
  address: {
    company: '.field__input[name="checkout[shipping_address][company]"]',
    address1: '.field__input[name="checkout[shipping_address][address1]"]',
    address2: '.field__input[name="checkout[shipping_address][address2]"]',
    city: '.field__input[name="checkout[shipping_address][city]"]',
    country: '.field__input[name="checkout[shipping_address][country]"]',
    province: '.field__input[name="checkout[shipping_address][province]"]',
    postcode: '.field__input[name="checkout[shipping_address][zip]"]',
  },
  lookupContainer: '[js-lookup="container"]',
  lookupResults: '[js-lookup="results"]',
  lookupNotice: '[js-lookup="notice"]',
  jumpPoint: '[js-lookup="link"]',
};

/**
 * Set global variables.
 */
let timer = {};
const key = theme.strings.loqateKey;

/*
* Create a new collection filter instance.
*/
export default () => {

  /**
   * Cache selectors.
   */
  let nodeSelectors = {
    address: {
      company: document.querySelector(selectors.address.company),
      address1: document.querySelector(selectors.address.address1),
      address2: document.querySelector(selectors.address.address2),
      city: document.querySelector(selectors.address.city),
      country: document.querySelector(selectors.address.country),
      postcode: document.querySelector(selectors.address.postcode),
    },
  };

  /**
   * Initialise.
   */
  function init() {
    if (Shopify.Checkout.step !== 'contact_information') {
      return;
    }

    setNodeSelectors();
    setEventListeners();
    addLookupContainer();
  }

  /**
   * Reset nodeSelectors each time the page refreshes.
   */
  function setNodeSelectors() {
    nodeSelectors = {
      address: {
        company: document.querySelector(selectors.address.company),
        address1: document.querySelector(selectors.address.address1),
        address2: document.querySelector(selectors.address.address2),
        city: document.querySelector(selectors.address.city),
        country: document.querySelector(selectors.address.country),
        postcode: document.querySelector(selectors.address.postcode),
      },
    };
  }

  /**
   * Set listeners.
   */
  function setEventListeners() {
    nodeSelectors.address.address1.addEventListener('keyup', lookupAddress);
    nodeSelectors.address.postcode.addEventListener('keyup', lookupAddress);
    nodeSelectors.address.country.addEventListener('change', handleCountryChange);

    document.addEventListener('click', (event) => {
      if (!event.target || typeof event.target.attributes['js-lookup'] === 'undefined') {
        return;
      }

      if (event.target.getAttribute('js-lookup') === 'result') {
        handleResultClick(event.target);
        return;
      }

      if (event.target.getAttribute('js-lookup') === 'link') {
        handleJumpPointClick();
      }
    });
  }

  /**
   * Looks up entered address/zip
   * @param {Object} event - Keyup event.
   */
  function lookupAddress(event) {
    if (!validKey(event)) {
      return;
    }

    featureScroll().init();

    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      const value = event.target.value;

      if (value.length && value.length > 3) {
        requestAddress(event.target.value);

      } else {
        setDefaultState();
      }
    }, 500);
  }

  /**
   * Validate that keyup is not a modifier key.
   * @param {Object} event - Keyup event.
   */
  function validKey(event) {
    const modiferKeycodes = [
      'Control',
      'Alt',
      'Meta',
      'Shift',
      'CapsLock',
      'Tab',
    ];

    return (!modiferKeycodes.includes(event.key));
  }

  /**
   * Gather information before request from Loqate API.
   * @param {String} value - Address search value.
   */
  function requestAddress(value) {
    let countryCode = 'GB';
    const selectedValue = nodeSelectors.address.country.value;
    countryCode = document.querySelector(`option[value="${selectedValue}"]`).getAttribute('data-code');

    const url = `https://api.addressy.com/Capture/Interactive/Find/v1.1/json3.ws?Key=${key}&Text=${value}&Origin=${countryCode}&Countries=${countryCode}&Language=en-gb`;

    loqateApiFindRequest(url);
  }

  /**
   * Make the request to the find API.
   * @param {String} url - Request URL.
   */
  function loqateApiFindRequest(url) {
    setLoadingState();

    axios.get(url)
      .then((response) => {
        let html = '';

        response.data.Items.forEach((item) => {
          html +=
          `
            <button
              class="lookup__result caption ${renderAddressArrow(item)}"
              type="button"
              data-id="${item.Id}"
              data-type="${item.Type}"
              js-lookup="result"
            >
              <span class="lookup__title">
                ${item.Text}
              </span>
              <span class="lookup__description">
                ${item.Description}
              </span>
            </button>
          `;
        });

        document.querySelector(selectors.lookupResults).innerHTML = html;
        setActiveState();
      })
      .catch((error) => {
        setErrorState();
        throw new Error(`Loqate find API error: ${error}`);
      });
  }

  /**
   * Renders show more arrow for results with multiple addresses.
   * @param {Object} item - Address item.
   */
  function renderAddressArrow(item) {
    if (item.Type === 'Address') {
      return '';
    }

    return 'button button--dark button--secondary';
  }

  /**
   * Sets the loading state of the lookup.
   */
  function setLoadingState() {
    nodeSelectors.address.address1.parentNode.classList.remove('is-active');
    nodeSelectors.address.address1.parentNode.classList.add('is-loading');
    nodeSelectors.address.address1.parentNode.classList.remove('is-invalid');

    document.querySelector(selectors.lookupContainer).setAttribute('aria-expanded', true);
    document.querySelector(selectors.lookupNotice).innerText = decodeHTMLEntities(theme.strings.lookupLoading);
  }

  /**
   * Sets the active state of the lookup.
   */
  function setActiveState() {
    nodeSelectors.address.address1.parentNode.classList.add('is-active');
    nodeSelectors.address.address1.parentNode.classList.remove('is-loading');
    nodeSelectors.address.address1.parentNode.classList.remove('is-invalid');

    document.querySelector(selectors.lookupContainer).setAttribute('aria-expanded', true);
    document.querySelector(selectors.lookupNotice).innerText = decodeHTMLEntities(theme.strings.lookupLoaded);
  }

  /**
   * Sets the error state
   */
  function setErrorState() {
    nodeSelectors.address.address1.parentNode.classList.remove('is-active');
    nodeSelectors.address.address1.parentNode.classList.remove('is-loading');
    nodeSelectors.address.address1.parentNode.classList.add('is-invalid');

    document.querySelector(selectors.lookupContainer).setAttribute('aria-expanded', true);
    document.querySelector(selectors.lookupNotice).innerText = decodeHTMLEntities(theme.strings.lookupError);
  }

  /**
   * Reverts to default state.
   */
  function setDefaultState() {
    nodeSelectors.address.address1.parentNode.classList.remove('is-active');
    nodeSelectors.address.address1.parentNode.classList.remove('is-loading');
    nodeSelectors.address.address1.parentNode.classList.remove('is-invalid');

    document.querySelector(selectors.lookupContainer).setAttribute('aria-expanded', false);
    document.querySelector(selectors.lookupNotice).innerText = '';
  }

  /**
   * Handle result click.
   * @param {HTMLElement} target - Clicked result.
   */
  function handleResultClick(target) {
    const id = target.getAttribute('data-id');
    const type = target.getAttribute('data-type');

    if (type === 'Address') {
      const url = `https://api.addressy.com/Capture/Interactive/Retrieve/v1/json3.ws?Key=${key}&Id=${id}`;
      loqateApiRetrieveRequest(url);

    } else {
      const url = `https://api.addressy.com/Capture/Interactive/Find/v1.1/json3.ws?Key=${key}&Container=${id}&Language=en-gb`;
      loqateApiFindRequest(url);
    }
  }

  /**
   * Make the request to the retrieve API.
   * @param {String} url - Request URL.
   */
  function loqateApiRetrieveRequest(url) {
    setLoadingState();

    axios.get(url)
      .then((response) => {
        populateFields(response.data.Items[0]);
        setDefaultState();
      })
      .catch((error) => {
        setErrorState();
        throw new Error(`Loqate retrieve API error: ${error}`);
      });
  }

  /**
   * Populates the shipping address fields with address.
   * @param {Object} address - Address response from API.
   */
  function populateFields(address) {
    nodeSelectors.address.address1.value = address.Line1;
    nodeSelectors.address.city.value = address.City;
    nodeSelectors.address.postcode.value = address.PostalCode;

    if (!address.Line3 === '') {
      nodeSelectors.address.address2.value = `${address.Line2},  ${address.Line3}`;
    } else {
      nodeSelectors.address.address2.value = address.Line2;
    }

    populateProvince(address);
  }

  /**
   * Update province field.
   * @param {Object} address - Address response from API.
   */
  function populateProvince(address) {
    const provinceSelect = document.querySelector(selectors.address.province);

    if (!provinceSelect) {
      return;
    }

    const country = nodeSelectors.address.country.value;
    let provinceCode = address.ProvinceCode;

    if (country === 'Ireland') {
      provinceCode = address.AdminAreaCode;
    }

    if (provinceCode !== '') {
      provinceSelect.value = provinceCode;
    }
  }

  /**
   * Changes focus to results container.
   */
  function handleJumpPointClick() {
    const lookupResults = document.querySelector(selectors.lookupResults);

    if (!lookupResults) {
      return;
    }

    lookupResults.firstChild.focus();
  }

  /**
   * Handle the shipping country field being changed.
   */
  function handleCountryChange() {
    nodeSelectors.address.address1.value = '';
    nodeSelectors.address.address1.parentNode.parentNode.classList.remove('field--show-floating-label');

    nodeSelectors.address.postcode.value = '';
    nodeSelectors.address.postcode.parentNode.parentNode.classList.remove('field--show-floating-label');
    setDefaultState();
  }

  /**
   * Adds lookup container markup.
   */
  function addLookupContainer() {
    if (document.querySelector('[js-lookup="container"]')) {
      return;
    }

    const lookupContainer = document.createElement('div');
    lookupContainer.classList.add('lookup');
    lookupContainer.setAttribute('aria-expanded', false);
    lookupContainer.setAttribute('aria-labelledby', 'Lookup-Label');
    lookupContainer.setAttribute('js-lookup', 'container');
    lookupContainer.setAttribute('js-simplebar', 'container');
    lookupContainer.innerHTML = `
      <span id="Lookup-Label" class="visually-hidden">
        ${decodeHTMLEntities(theme.strings.lookupLabel)}
      </span>

      <div class="lookup__loader loader"></div>

      <div class="lookup__results" js-lookup="results"></div>

      <div class="lookup__error caption">
        ${decodeHTMLEntities(theme.strings.lookupError)}
      </div>

      <span class="visually-hidden" role="status" aria-live="polite" js-lookup="notice"></span>
    `;

    nodeSelectors.address.address1.parentNode.appendChild(lookupContainer);
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
